import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMapBox } from "../components/BalloonEventsMapBox";
import { BeatAPISponsorRepository, SponsorModel, PartnerModel } from "ts-core";
import { useApi } from "react-ui";

interface PartnerInfo {
  sponsors: SponsorModel[];
  regionalPartners: PartnerModel[];
  findPartner: (id: string) => SponsorModel | PartnerModel | undefined;
  /**
   * Returns the partner from the locally loaded set if present; otherwise
   * fetches it directly by id (independent of the current map position).
   * Successful fetches are cached so subsequent calls are cheap.
   */
  fetchPartnerById: (id: string) => Promise<SponsorModel | PartnerModel | null>;
}
const PartnersContext = createContext<PartnerInfo | undefined>(undefined);

type SponsorProviderProps = {
  children: ReactNode;
};

export const SponsorsProvider: React.FC<SponsorProviderProps> = ({
  children,
}) => {
  const [sponsors, setSponsors] = useState<SponsorModel[]>([]);
  const [regionalPartners, setRegionalPartners] = useState<PartnerModel[]>([]);
  const [fetchedById, setFetchedById] = useState<Record<string, PartnerModel | SponsorModel>>({});
  const { apiEndpoints, apiKey } = useApi();
  const mapBoxInfo = useMapBox();
  const map = mapBoxInfo?.map;

  const repositoryRef = useRef<BeatAPISponsorRepository>(
    new BeatAPISponsorRepository(
      apiEndpoints.partnersByUserLocation,
      apiKey || '',
      apiEndpoints.partners
    )
  )
  const initialized = useRef(false);

  async function onMapboxMoveEnd(e: any) {
    loadSponsors(e.target.getBounds());
  }

  useEffect(() => {
    repositoryRef.current.url = apiEndpoints.partnersByUserLocation
    repositoryRef.current.partnersBaseUrl = apiEndpoints.partners
  }, [apiEndpoints])

  async function loadSponsors(bounds: mapboxgl.LngLatBounds) {
    const sponsorData = await repositoryRef.current.get(bounds)
    setSponsors(sponsorData.sponsors)
    setRegionalPartners(sponsorData.regionalPartners)
  }


  useEffect(() => {
    if (!map || !apiEndpoints || initialized.current) return;
    initialized.current = true;
    map.on("moveend", onMapboxMoveEnd);
    loadSponsors(map.getBounds());
  }, [apiEndpoints, map]);

  const findPartner = (id: string) =>
    sponsors.find(s => s.id === id) || regionalPartners.find(r => r.id === id) || fetchedById[id];

  const fetchPartnerById = async (id: string) => {
    const local = findPartner(id);
    if (local) return local;
    const fetched = await repositoryRef.current.getById(id);
    if (fetched) {
      setFetchedById(prev => ({ ...prev, [id]: fetched }));
    }
    return fetched;
  };

  return (
    <PartnersContext.Provider value={{ regionalPartners, sponsors, findPartner, fetchPartnerById }}>
      {children}
    </PartnersContext.Provider>
  );
};

export function usePartners(): PartnerInfo {
  const context = useContext(PartnersContext);
  if (!context) {
    throw new Error("useSponsors must be used within a SponsorsProvider");
  }
  return context;
}
